import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReorderRecommendations = ({ recommendations, onRefresh }) => {
  const [sortBy, setSortBy] = useState('priority');
  const [filterPriority, setFilterPriority] = useState('all');

  // Filter and sort recommendations
  const filteredRecommendations = recommendations
    ?.filter(rec => filterPriority === 'all' || rec?.priority === filterPriority)
    ?.sort((a, b) => {
      if (sortBy === 'priority') {
        const priorityOrder = { 'Critical': 1, 'High': 2, 'Medium': 3, 'Low': 4 };
        return priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
      } else if (sortBy === 'confidence') {
        return parseFloat(b?.confidence_score) - parseFloat(a?.confidence_score);
      } else if (sortBy === 'quantity') {
        return a?.current_quantity - b?.current_quantity;
      }
      return 0;
    });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-error';
      case 'High': return 'text-warning';
      case 'Medium': return 'text-info';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityBgColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-error/10 border-error';
      case 'High': return 'bg-warning/10 border-warning';
      case 'Medium': return 'bg-info/10 border-info';
      default: return 'bg-muted/10 border-border';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'Critical': return 'AlertCircle';
      case 'High': return 'AlertTriangle';
      case 'Medium': return 'Info';
      default: return 'CheckCircle';
    }
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-1">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
            Reorder Recommendations
          </h2>
          <p className="text-sm text-muted-foreground">
            ML-based insights with confidence scoring and suggested quantities
          </p>
        </div>
        <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
          <Icon name="ShoppingCart" size={20} color="var(--color-warning)" />
        </div>
      </div>
      {/* Filters and Sort */}
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">Priority:</label>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          >
            <option value="all">All Priorities</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">Sort By:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          >
            <option value="priority">Priority</option>
            <option value="confidence">Confidence Score</option>
            <option value="quantity">Current Quantity</option>
          </select>
        </div>
      </div>
      {/* Recommendations List */}
      {filteredRecommendations?.length === 0 ? (
        <div className="py-12 text-center">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
          <p className="text-foreground font-medium mb-1">All Stock Levels Healthy</p>
          <p className="text-sm text-muted-foreground">No reorder recommendations at this time</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRecommendations?.map((rec) => (
            <div
              key={rec?.product_id}
              className={`border rounded-lg p-4 transition-smooth hover:shadow-elevation-1 ${getPriorityBgColor(rec?.priority)}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon 
                      name={getPriorityIcon(rec?.priority)} 
                      size={18} 
                      className={getPriorityColor(rec?.priority)} 
                    />
                    <h3 className="font-semibold text-foreground">{rec?.product_name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(rec?.priority)}`}>
                      {rec?.priority}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground">SKU</p>
                      <p className="text-sm font-medium text-foreground">{rec?.sku}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Category</p>
                      <p className="text-sm font-medium text-foreground">{rec?.category}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Current Stock</p>
                      <p className="text-sm font-medium text-foreground">{rec?.current_quantity}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Reorder Level</p>
                      <p className="text-sm font-medium text-foreground">{rec?.reorder_level}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Avg Daily Usage</p>
                      <p className="text-sm font-medium text-primary">{parseFloat(rec?.avg_daily_usage || 0)?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Recommended Qty</p>
                      <p className="text-sm font-semibold text-success">{rec?.recommended_order_qty}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Days Until Stockout</p>
                      <p className={`text-sm font-medium ${
                        rec?.days_until_stockout < 7 ? 'text-error' : 
                        rec?.days_until_stockout < 14 ? 'text-warning' : 'text-foreground'
                      }`}>
                        {rec?.days_until_stockout < 999 ? `${rec?.days_until_stockout} days` : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Confidence Score</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-background rounded-full h-2 overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all"
                            style={{ width: `${rec?.confidence_score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-foreground">{rec?.confidence_score}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Lightbulb" size={14} />
                    <span>
                      Recommendation: Order {rec?.recommended_order_qty} units to maintain 30-day supply based on usage patterns
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 whitespace-nowrap"
                  onClick={() => {
                    // In a real app, this would trigger order creation
                    alert(`Creating order for ${rec?.product_name}\nQuantity: ${rec?.recommended_order_qty}`);
                  }}
                >
                  <Icon name="ShoppingCart" size={14} />
                  Create Order
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Summary Stats */}
      {filteredRecommendations?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-error">
                {recommendations?.filter(r => r?.priority === 'Critical')?.length}
              </p>
              <p className="text-xs text-muted-foreground">Critical</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-warning">
                {recommendations?.filter(r => r?.priority === 'High')?.length}
              </p>
              <p className="text-xs text-muted-foreground">High Priority</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-info">
                {recommendations?.filter(r => r?.priority === 'Medium')?.length}
              </p>
              <p className="text-xs text-muted-foreground">Medium Priority</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">
                {recommendations?.reduce((sum, r) => sum + (r?.recommended_order_qty || 0), 0)}
              </p>
              <p className="text-xs text-muted-foreground">Total Units Recommended</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReorderRecommendations;